import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatbot } from '../../context/chatbot-provider';
import { useCart } from '../../context/cart-provider';
import { useSites } from '../../context/sites-provider';
import { useCurrency } from '../../context/currency-context';
import { useLanguage } from '../../context/language-provider';
import { FaTimes, FaPaperPlane, FaRobot, FaUser, FaExclamationTriangle, FaComments } from 'react-icons/fa';
import { sendMessageToAgent, AVAILABLE_AGENTS, createNewSession } from '../../services/chatbotService';
import parse from 'html-react-parser';
import TypingIndicator from './TypingIndicator';
import { RESPONSE_TYPES } from '../../types/chatbot-response';
import { CartSummaryRenderer, OrderListRenderer, ProductListRenderer, ProductSelectionRenderer, AddressListRenderer, AccountDetailsRenderer, ReturnListRenderer, ReturnDetailsRenderer, ActionButtonsRenderer } from './ResponseRenderers';
import { getSimulatedResponse } from '../../services/demoResponses';

const ChatbotPanel = () => {
  const navigate = useNavigate();
  const { isOpen, closeChatbot, messages, addMessage, clearMessages, isLoading, setIsLoading } = useChatbot();
  const { cartAccount, syncCart } = useCart();
  const { currentSite } = useSites();
  const { activeCurrency } = useCurrency();
  const { currentLanguage } = useLanguage();
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Get context data for AI service
  const getContextData = () => {
    return {
      cartId: cartAccount?.id || null,
      siteId: currentSite || null,
      currency: activeCurrency?.code || null,
      language: currentLanguage || null
    };
  };

  // Parse markdown account format into structured account data
  const parseAccountMarkdown = (markdownText) => {
    const lines = markdownText.split('\n');
    const personalInfo = {};
    const addresses = [];
    let currentAddress = null;
    let inAddressesSection = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check for Account Details section
      if (line.includes('**Account Details:**')) {
        continue;
      }
      
      // Check for Addresses section
      if (line.includes('**Addresses:**')) {
        inAddressesSection = true;
        continue;
      }
      
      // Parse personal info fields
      if (!inAddressesSection && line.includes('**') && line.includes(':')) {
        const match = line.match(/\*\*(.+?):\*\*\s*(.+)/);
        if (match) {
          const field = match[1].toLowerCase().replace(/\s+/g, '');
          const value = match[2];
          
          switch (field) {
            case 'name':
              personalInfo.name = value;
              break;
            case 'email':
              personalInfo.email = value;
              break;
            case 'phone':
              personalInfo.phone = value;
              break;
            case 'company':
              personalInfo.company = value;
              break;
            case 'customernumber':
              personalInfo.customerNumber = value;
              break;
            case 'businessmodel':
              personalInfo.businessModel = value;
              break;
            case 'preferredlanguage':
              personalInfo.preferredLanguage = value;
              break;
            case 'preferredcurrency':
              personalInfo.preferredCurrency = value;
              break;
            case 'preferredsite':
              personalInfo.preferredSite = value;
              break;
            case 'lastlogin':
              personalInfo.lastLogin = value;
              break;
          }
        }
      }
      
      // Parse addresses
      if (inAddressesSection && line && !line.includes('**')) {
        // Check for address number (e.g., "1. Lukasz Stypka")
        if (line.match(/^\d+\./)) {
          if (currentAddress) {
            addresses.push(currentAddress);
          }
          
          const addressMatch = line.match(/^\d+\.\s*(.+?),\s*(.+?),\s*(\d+)\s+(.+?),\s*(.+?)\s+\((.+)\)$/);
          if (addressMatch) {
            const tags = addressMatch[6].split(',').map(tag => tag.trim());
            currentAddress = {
              addressId: `addr_${addresses.length + 1}`,
              name: addressMatch[1],
              addressLine1: addressMatch[2],
              city: addressMatch[4],
              state: addressMatch[5],
              postalCode: addressMatch[3],
              country: addressMatch[5],
              tags: tags
            };
          }
        }
      }
    }
    
    // Add the last address
    if (currentAddress) {
      addresses.push(currentAddress);
    }
    
    return {
      personalInfo: personalInfo,
      addresses: addresses,
      message: 'Here are the details about your account.'
    };
  };

  // Parse markdown address format into structured address data
  const parseAddressMarkdown = (markdownText) => {
    const addresses = [];
    const lines = markdownText.split('\n');
    let currentAddress = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check for address header (e.g., "**1. Lukasz Stypka**")
      if (line.startsWith('**') && line.endsWith('**')) {
        if (currentAddress) {
          addresses.push(currentAddress);
        }
        const nameMatch = line.match(/\*\*\d+\.\s*(.+)\*\*/);
        currentAddress = {
          addressId: `addr_${addresses.length + 1}`,
          name: nameMatch ? nameMatch[1] : 'Unknown',
          company: null,
          addressLine1: '',
          addressLine2: null,
          city: '',
          state: '',
          postalCode: '',
          country: '',
          countryCode: '',
          tags: [],
          isDefault: false
        };
      }
      // Check for address lines (not starting with ** or Tags:)
      else if (currentAddress && line && !line.startsWith('Tags:')) {
        if (!currentAddress.addressLine1) {
          currentAddress.addressLine1 = line;
        } else if (!currentAddress.city) {
          // Parse city, state, postal code, country
          const addressMatch = line.match(/^(.+),\s*(.+)\s+(\d+),\s*(.+)\s+\(State:\s*(.+)\)$/);
          if (addressMatch) {
            currentAddress.city = addressMatch[1];
            currentAddress.state = addressMatch[5];
            currentAddress.postalCode = addressMatch[3];
            currentAddress.country = addressMatch[4];
            currentAddress.countryCode = addressMatch[4] === 'DE' ? 'DE' : 'US';
          }
        }
      }
      // Check for tags line
      else if (currentAddress && line.startsWith('Tags:')) {
        const tagsText = line.replace('Tags:', '').trim();
        const tags = tagsText.split(',').map(tag => tag.trim());
        currentAddress.tags = tags;
        currentAddress.isDefault = tags.includes('Default');
      }
    }
    
    // Add the last address
    if (currentAddress) {
      addresses.push(currentAddress);
    }
    
    return {
      addresses: addresses,
      message: 'Here are your saved addresses.'
    };
  };

  const renderStructuredResponse = (message) => {
    // Check if the message.text contains a JSON string that needs to be parsed
    let parsedMessage = message;
    
    if (message.sender === 'bot' && message.text && typeof message.text === 'string') {
      try {
        let jsonString = message.text;
        
        // Check if the message is wrapped in markdown code blocks
        if (jsonString.includes('```json') && jsonString.includes('```')) {
          // Extract JSON from markdown code blocks
          const jsonMatch = jsonString.match(/```json\s*([\s\S]*?)\s*```/);
          if (jsonMatch && jsonMatch[1]) {
            jsonString = jsonMatch[1].trim();
          }
        }
        
        // Try to parse the JSON string
        const jsonData = JSON.parse(jsonString);
        if (jsonData.type && jsonData.data) {
          // Special handling for account details in current format
          if (jsonData.type === 'text' && jsonData.data && jsonData.data.message && 
              jsonData.data.message.includes('**Account Details:**')) {
            // Convert markdown account format to structured account details
            const accountData = parseAccountMarkdown(jsonData.data.message);
            parsedMessage = {
              ...message,
              type: RESPONSE_TYPES.ACCOUNT_DETAILS,
              data: accountData,
              text: jsonData.message || message.text
            };
          }
          // Special handling for address data in current format
          else if (jsonData.type === 'text' && jsonData.data && jsonData.data.message && 
              (jsonData.data.message.includes('**') || jsonData.data.message.includes('Tags:')) &&
              !jsonData.data.message.includes('**Account Details:**')) {
            // Convert markdown address format to structured address list
            const addressData = parseAddressMarkdown(jsonData.data.message);
            parsedMessage = {
              ...message,
              type: RESPONSE_TYPES.ADDRESS_LIST,
              data: addressData,
              text: jsonData.message || message.text
            };
          } else {
            // If it's a valid structured response, use the parsed data
            parsedMessage = {
              ...message,
              type: jsonData.type,
              data: jsonData.data,
              text: jsonData.message || message.text // Use the inner message as fallback text
            };
          }
        }
      } catch (e) {
        // If parsing fails, continue with original message
        console.log('Message is not JSON, using as plain text:', e.message);
      }
    }

    // If we don't have structured data, return plain text
    if (!parsedMessage.type || !parsedMessage.data) {
      return parsedMessage.sender === 'bot' ? parse(parsedMessage.text) : parsedMessage.text;
    }

    const handleButtonClick = (button) => {
      if (button.action === 'send_message' && button.message) {
        const userMessage = {
          id: Date.now(),
          text: button.message,
          sender: 'user',
          timestamp: new Date()
        };
        addMessage(userMessage);
        setIsLoading(true);
        // Simulate response for demo
        setTimeout(() => {
          const botMessage = {
            id: Date.now() + 1,
            text: `Processing: ${button.message}`,
            sender: 'bot',
            timestamp: new Date()
          };
          addMessage(botMessage);
          setIsLoading(false);
        }, 1000);
      }
    };

    const handleAddToCart = (productId) => {
      // Handle both single product ID and array of product IDs
      let message;
      if (Array.isArray(productId)) {
        message = `Add products ${productId.join(', ')} to my cart`;
      } else {
        message = `Add product ${productId} to my cart`;
      }
      
      const userMessage = {
        id: Date.now(),
        text: message,
        sender: 'user',
        timestamp: new Date()
      };
      addMessage(userMessage);
      setIsLoading(true);

      // Send the message to AI service
      setTimeout(async () => {
        try {
          const contextData = getContextData();
          const response = await sendMessageToAgent(message, AVAILABLE_AGENTS.ORDER_CHECKER, contextData);
          
          const botMessage = {
            id: Date.now() + 1,
            text: response.message,
            sender: 'bot',
            timestamp: new Date(),
            agentId: response.agentId,
            sessionId: response.sessionId,
            type: response.type || RESPONSE_TYPES.TEXT,
            data: response.data || null
          };
          addMessage(botMessage);
          
          // Refresh cart in main application after adding product
          syncCart();
        } catch (error) {
          console.error('Add to cart error:', error);
          const errorMessage = {
            id: Date.now() + 1,
            text: 'Sorry, I encountered an error adding the product to your cart. Please try again.',
            sender: 'bot',
            timestamp: new Date(),
            isError: true
          };
          addMessage(errorMessage);
        } finally {
          setIsLoading(false);
        }
      }, 100);
    };

    const handleVariantSelect = (selectedVariants) => {
      const message = `I've selected variants: ${selectedVariants.join(', ')}`;
      const userMessage = {
        id: Date.now(),
        text: message,
        sender: 'user',
        timestamp: new Date()
      };
      addMessage(userMessage);
      
      // You can add additional logic here to handle variant selection
      console.log('Selected variants:', selectedVariants);
    };

    switch (parsedMessage.type) {
      case RESPONSE_TYPES.CART_SUMMARY:
        return <CartSummaryRenderer data={parsedMessage.data} onCheckout={handleCheckout} />;
      case RESPONSE_TYPES.ORDER_LIST:
        return <OrderListRenderer data={parsedMessage.data} />;
      case RESPONSE_TYPES.PRODUCT_LIST:
        return <ProductListRenderer data={parsedMessage.data} onAddToCart={handleAddToCart} />;
      case RESPONSE_TYPES.PRODUCT_SELECTION:
        return <ProductSelectionRenderer key="product-selection" data={parsedMessage.data} onVariantSelect={handleVariantSelect} onAddToCart={handleAddToCart} />;
      case RESPONSE_TYPES.ADDRESS_LIST:
        return <AddressListRenderer data={parsedMessage.data} />;
      case RESPONSE_TYPES.ACCOUNT_DETAILS:
        return <AccountDetailsRenderer data={parsedMessage.data} />;
      case RESPONSE_TYPES.RETURN_LIST:
        return <ReturnListRenderer data={parsedMessage.data} />;
      case RESPONSE_TYPES.RETURN_DETAILS:
        return <ReturnDetailsRenderer data={parsedMessage.data} />;
      case RESPONSE_TYPES.ACTION_BUTTONS:
        return <ActionButtonsRenderer data={parsedMessage.data} onButtonClick={handleButtonClick} />;
      default:
        return parsedMessage.sender === 'bot' ? parse(parsedMessage.text) : parsedMessage.text;
    }
  };

  const handleNewSession = () => {
    // Generate new session ID
    const newSessionId = createNewSession();
    
    // Clear current conversation
    clearMessages();
    
    // Don't add any messages - this will show the home page state
  };

  const handleCheckout = () => {
    // Close the chatbot dialog
    closeChatbot();
    
    // Navigate to checkout page with tenant
    const tenant = currentSite?.tenant || 'main';
    navigate(`/${tenant}/checkout`);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    addMessage(userMessage);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);
    console.log('Loading state set to true');

    try {
      // Call the real AI service with context
      const contextData = getContextData();
      const response = await sendMessageToAgent(currentMessage, AVAILABLE_AGENTS.ORDER_CHECKER, contextData);
      
      const botMessage = {
        id: Date.now() + 1,
        text: response.message,
        sender: 'bot',
        timestamp: new Date(),
        agentId: response.agentId,
        sessionId: response.sessionId,
        type: response.type || RESPONSE_TYPES.TEXT, // Default to text if no type provided
        data: response.data || null
      };
      addMessage(botMessage);
      
      // Refresh cart if the response indicates cart changes or contains cart/product related keywords
      if (response.type === RESPONSE_TYPES.CART_SUMMARY || 
          response.type === RESPONSE_TYPES.PRODUCT_SELECTION ||
          (response.message && (
            response.message.toLowerCase().includes('cart') ||
            response.message.toLowerCase().includes('added') ||
            response.message.toLowerCase().includes('product')
          ))) {
        syncCart();
      }
      
      // Demo responses (commented out - uncomment to test UI without API)
      /*
      const response = getSimulatedResponse(currentMessage);
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
      const botMessage = {
        id: Date.now() + 1,
        text: response.message,
        sender: 'bot',
        timestamp: new Date(),
        agentId: response.agentId,
        sessionId: response.sessionId,
        type: response.type,
        data: response.data
      };
      addMessage(botMessage);
      */
    } catch (error) {
      // Handle API errors
      console.error('Chatbot API Error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, I encountered an error. Please try again or contact support if the problem persists.',
        sender: 'bot',
        timestamp: new Date(),
        isError: true
      };
      addMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickAction = (message) => {
    // Set the message in the input field instead of sending directly
    setInputMessage(message);
    
    // Focus the input field after a short delay to ensure it's rendered
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] flex overflow-hidden chatbot-panel">
        {/* Dark Sidebar */}
        <div className="w-80 bg-slate-900 flex flex-col">
          {/* Sidebar Header */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <FaRobot className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-white font-semibold">Emporix Agent</h2>
                <p className="text-slate-400 text-sm">AI Assistant</p>
              </div>
            </div>
          </div>

          {/* Sidebar Menu */}
          <div className="flex-1 p-4">
            <nav className="space-y-2">
              <div className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-blue-600 text-white">
                <FaComments className="text-sm" />
                <span className="text-sm font-medium">Chat</span>
              </div>
              <button 
                onClick={handleNewSession}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
              >
                <FaRobot className="text-sm" />
                <span className="text-sm font-medium">New Session</span>
              </button>
            </nav>

            {/* Quick Actions */}
            <div className="mt-8">
              <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-3">Quick Actions</h3>
              <div className="space-y-1">
                <button 
                  onClick={() => handleQuickAction('Show my cart')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  Show my cart
                </button>
                <button 
                  onClick={() => handleQuickAction('Show my recent orders')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  Recent orders
                </button>
                <button 
                  onClick={() => handleQuickAction('Track my order')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  Track order
                </button>
                <button 
                  onClick={() => handleQuickAction('Show my account details')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  My account
                </button>
                <button 
                  onClick={() => handleQuickAction('Show my addresses')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  My addresses
                </button>
                <button 
                  onClick={() => handleQuickAction('Show my returns')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  My returns
                </button>
                <button 
                  onClick={() => handleQuickAction('Help me search for products')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  Product search
                </button>
                <button 
                  onClick={() => handleQuickAction('What can you help me with?')}
                  className="w-full text-left px-3 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm"
                >
                  Help & support
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-slate-700">
            <button
              onClick={closeChatbot}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
            >
              <FaTimes className="text-sm" />
              <span className="text-sm font-medium">Close Chat</span>
            </button>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Chat Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Chat Assistant</h1>
                <p className="text-sm text-gray-500">How can I help you today?</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-500">Online</span>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full px-4 py-6">
                <div className="text-center max-w-md">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaRobot className="text-2xl text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome to Emporix AI Assistant</h3>
                  <p className="text-gray-600 mb-6">
                    I can help you with orders, products, cart management, and more. Try one of these quick actions:
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <button 
                      onClick={() => handleQuickAction('Show me my cart please')}
                      className="p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="text-sm font-medium text-gray-900">Show my cart</div>
                      <div className="text-xs text-gray-500 mt-1">View current items</div>
                    </button>
                    <button 
                      onClick={() => handleQuickAction('Show me my recent orders')}
                      className="p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="text-sm font-medium text-gray-900">Recent orders</div>
                      <div className="text-xs text-gray-500 mt-1">Check order history</div>
                    </button>
                    <button 
                      onClick={() => handleQuickAction('Track my latest order')}
                      className="p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="text-sm font-medium text-gray-900">Track order</div>
                      <div className="text-xs text-gray-500 mt-1">Get shipping updates</div>
                    </button>
                    <button 
                      onClick={() => handleQuickAction('Show me product recommendations')}
                      className="p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="text-sm font-medium text-gray-900">Find products</div>
                      <div className="text-xs text-gray-500 mt-1">Search catalog</div>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6 max-w-4xl mx-auto px-4 py-6">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex items-start space-x-3 max-w-[85%] ${
                      message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                    }`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        message.sender === 'user' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-white border border-gray-200 text-gray-600'
                      }`}>
                        {message.sender === 'user' ? <FaUser className="text-sm" /> : <FaRobot className="text-sm" />}
                      </div>
                      <div className={`rounded-xl shadow-sm ${
                        message.sender === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-200'
                      }`}>
                        {message.isError && (
                          <div className="flex items-center p-3 pb-0">
                            <FaExclamationTriangle className="text-red-500 text-sm mr-2" />
                            <span className="text-sm font-medium text-red-600">Error</span>
                          </div>
                        )}
                        <div className={`${message.sender === 'user' ? 'p-3' : ''}`}>
                          {message.sender === 'user' ? (
                            <div className="text-sm">{message.text}</div>
                          ) : (
                            <div className="text-sm text-gray-800">
                              {renderStructuredResponse(message)}
                            </div>
                          )}
                        </div>
                        <div className={`px-3 pb-2 text-xs ${
                          message.sender === 'user' ? 'text-blue-200' : 'text-gray-500'
                        }`}>
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {message.agentId && (
                            <span className="ml-2">• {message.agentId}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && <TypingIndicator variant="default" />}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4 bg-white">
            <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto">
              <div className="flex space-x-3">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isLoading ? "AI is responding..." : "Type your message..."}
                  className={`flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={!inputMessage.trim() || isLoading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 font-medium"
                >
                  <FaPaperPlane className="text-sm" />
                  <span>Send</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotPanel;
